/**
 * Polyfills de built-ins ausentes em navegadores antigos — ex.: MIUI Browser 12.x
 * (Chromium ~71), que ainda aparece no Sentry em aparelhos Android baratos.
 *
 * O downlevel de sintaxe do build (browserslist: Chrome >= 79) converte sintaxe, mas NÃO
 * injeta polyfill de API. Por isso o bundle carrega nesses aparelhos e só quebra quando
 * algum destes built-ins é chamado:
 *
 * - Object.fromEntries (Chrome 73)  — usado pelo @angular/animations ao montar keyframes
 *                                     (TREINO-5G: "Object.fromEntries is not a function").
 * - Promise.allSettled (Chrome 76)  — usado por src/app/services/chunk-load-recovery.ts.
 * - structuredClone    (Chrome 98)  — usado em payments, anamnese e activities.
 *
 * Cada um só é instalado quando ausente; navegadores modernos usam a implementação nativa.
 * Este arquivo deve ser importado antes do zone.js.
 */

const globalScope: any = typeof globalThis !== 'undefined' ? globalThis : window;

/***************************************************************************************************
 * Object.fromEntries (ES2019)
 */
if (typeof (Object as any).fromEntries !== 'function') {
  (Object as any).fromEntries = (entries: Iterable<readonly [PropertyKey, unknown]>) => {
    if (entries === null || entries === undefined) {
      throw new TypeError('Object.fromEntries requires an iterable argument');
    }
    const result: Record<PropertyKey, unknown> = {};
    for (const entry of entries as any) {
      result[entry[0]] = entry[1];
    }
    return result;
  };
}

/***************************************************************************************************
 * Promise.allSettled (ES2020)
 */
if (typeof (Promise as any).allSettled !== 'function') {
  (Promise as any).allSettled = (values: Iterable<unknown>) =>
    Promise.all(
      Array.from(values, (value) =>
        Promise.resolve(value).then(
          (v) => ({ status: 'fulfilled', value: v }),
          (reason) => ({ status: 'rejected', reason }),
        ),
      ),
    );
}

/***************************************************************************************************
 * structuredClone (Chrome 98)
 *
 * Cobre o que a app realmente clona: objetos e arrays de estado, Date, Map, Set e referências
 * circulares. Não cobre casos do algoritmo nativo que a app não usa (Blob, File, ArrayBuffer,
 * RegExp, Error, transferables).
 */
function deepClone<T>(value: T, seen: WeakMap<object, unknown>): T {
  if (value === null || typeof value !== 'object') return value;

  const source = value as unknown as object;
  if (seen.has(source)) return seen.get(source) as T;

  if (value instanceof Date) return new Date(value.getTime()) as unknown as T;

  if (Array.isArray(value)) {
    const copy: unknown[] = [];
    seen.set(source, copy);
    for (const item of value) copy.push(deepClone(item, seen));
    return copy as unknown as T;
  }

  if (value instanceof Map) {
    const copy = new Map();
    seen.set(source, copy);
    value.forEach((v, k) => copy.set(deepClone(k, seen), deepClone(v, seen)));
    return copy as unknown as T;
  }

  if (value instanceof Set) {
    const copy = new Set();
    seen.set(source, copy);
    value.forEach((v) => copy.add(deepClone(v, seen)));
    return copy as unknown as T;
  }

  const copy: Record<string, unknown> = {};
  seen.set(source, copy);
  for (const key of Object.keys(value as Record<string, unknown>)) {
    copy[key] = deepClone((value as Record<string, unknown>)[key], seen);
  }
  return copy as unknown as T;
}

if (typeof globalScope.structuredClone !== 'function') {
  globalScope.structuredClone = <T>(value: T): T => deepClone(value, new WeakMap());
}

export {};
